/**
 * useRoleNavigation Hook
 *
 * Role-aware navigation helpers + capability flags for screens.
 * 2026-05-13: migrated off legacy `user.profile.user_type` onto
 * position_type + permissions. The hook's PUBLIC surface (returned flag
 * names) is preserved so consumers don't have to change.
 *
 * Role mapping (legacy → new):
 *   'org_employee'  → isEmployee(user)
 *   'org_executive' → isOrgExecutive(user)            // MANAGER OR DIRECTOR
 *   'executive'     → isDG(user)
 *   'strategy_team' → canCreateStrategicGoals(user)   // capability-based
 *   'superuser'     → user.is_superuser
 */
import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from '../contexts/AuthContext';
import { MainStackParamList } from '../navigation/MainNavigator';
import {
  isEmployee as isEmployeeHelper,
  isSectionHead as isSectionHeadHelper,
  isDirector as isDirectorHelper,
  isOrgExecutive as isOrgExecutiveHelper,
  isDG as isDGHelper,
  canCreateStrategicGoals,
} from '../utils/userPermissions';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

export function useRoleNavigation() {
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAuth();
  const dataEntryOpen = user?.data_entry_open !== false;

  // ─── Derived role flags ──────────────────────────────────────────────────
  const isSuperuser = !!user?.is_superuser;
  const isOrgEmployee = isEmployeeHelper(user);
  const isSectionHead = isSectionHeadHelper(user);
  const isDirector = isDirectorHelper(user);
  const isOrgExecutive = isOrgExecutiveHelper(user);
  const isDecisionMaker = isDGHelper(user);
  const isStrategyTeam = canCreateStrategicGoals(user);  // capability-based
  const userPermissions = user?.permissions || [];

  // ============================================================
  // Permission Flags for different roles (per DTS 2.2)
  // - org_executive: ambiguous bucket — covers BOTH Section Head (MANAGER,
  //   proposer) AND Director (DIRECTOR, approver). canApprove now keys
  //   on hrcpms.review.approve_reject so it only fires for Director.
  // - executive (DG): VIEW only
  // - org_employee: Can add/edit/delete (subject to data_entry_open)
  // - strategy_team: final approver — also gets canApprove via the perm
  // - superuser: Full access
  // ============================================================

  // Is data entry closed for this org_employee?
  const isDataEntryClosed = useMemo(() => {
    return isOrgEmployee && !dataEntryOpen;
  }, [isOrgEmployee, dataEntryOpen]);

  // Can add new items (superuser, org_employee when data entry is open)
  const canAdd = useMemo(() => {
    if (isSuperuser) return true;
    if (isOrgEmployee) return dataEntryOpen;
    return false;
  }, [isSuperuser, isOrgEmployee, dataEntryOpen]);

  // Can edit goals, initiatives, KPIs, etc. (superuser, org_employee when data entry is open)
  const canEdit = useMemo(() => {
    if (isSuperuser) return true;
    if (isOrgEmployee) return dataEntryOpen;
    return false;
  }, [isSuperuser, isOrgEmployee, dataEntryOpen]);

  // DTS 2.2 "مراجعة الأعمال واعتماد/رفض": Director + Strategy Team approve/reject.
  // Permission-based so a Section Head (proposer) doesn't accidentally
  // get the approve UI.
  const canApprove = useMemo(() => {
    if (isSuperuser) return true;
    return userPermissions.includes('hrcpms.review.approve_reject');
  }, [isSuperuser, userPermissions]);

  // Can delete items (superuser, org_employee when data entry is open)
  const canDelete = useMemo(() => {
    if (isSuperuser) return true;
    if (isOrgEmployee) return dataEntryOpen;
    return false;
  }, [isSuperuser, isOrgEmployee, dataEntryOpen]);

  // Is this user view-only?
  //   DG  → strategic oversight, no data entry
  //   Director → approver of operational items proposed by Section Head
  //   Strategy Team → strategic goals only; operational items are not theirs to add
  //   Employee with closed data entry → frozen
  // Section Head (MANAGER) is intentionally NOT view-only: per DTS 2.2 they
  // propose operational items (process / service / activity / sub-process /
  // unit goal). The legacy `isOrgExecutive` bucket lumped MANAGER + DIRECTOR
  // together and silently redirected Section Heads off every Add* screen.
  const isViewOnly = useMemo(() => {
    if (isDecisionMaker || isDirector || isStrategyTeam) return true;
    if (isOrgEmployee && !dataEntryOpen) return true;
    return false;
  }, [isDecisionMaker, isDirector, isStrategyTeam, isOrgEmployee, dataEntryOpen]);

  // Can view all organizations (strategy_team, superuser, DG)
  const canViewAllOrganizations = useMemo(() => {
    return isStrategyTeam || isSuperuser || isDecisionMaker;
  }, [isStrategyTeam, isSuperuser, isDecisionMaker]);

  // Can see pending items and approve/reject (org_executive, superuser)
  const canSeePending = useMemo(() => {
    return isOrgExecutive || isSuperuser;
  }, [isOrgExecutive, isSuperuser]);

  // Can see notifications (everyone except DG)
  const canSeeNotifications = useMemo(() => {
    return !isDecisionMaker;
  }, [isDecisionMaker]);

  // Can view reports (strategy_team, superuser, DG, org_executive)
  const canExport = useMemo(() => {
    return isStrategyTeam || isSuperuser || isDecisionMaker || isOrgExecutive;
  }, [isStrategyTeam, isSuperuser, isDecisionMaker, isOrgExecutive]);

  // Can add comments (org_employee, org_executive, strategy_team, superuser — NOT DG)
  const canComment = useMemo(() => {
    return isOrgEmployee || isOrgExecutive || isStrategyTeam || isSuperuser;
  }, [isOrgEmployee, isOrgExecutive, isStrategyTeam, isSuperuser]);

  // True for users who land on StrategyStack (Strategy Team, DG, superuser)
  const usesStrategyStack = isStrategyTeam || isDecisionMaker || isSuperuser;

  /**
   * Navigate to home/dashboard based on user role
   * - org_employee, org_executive → DashboardStack/Dashboard
   * - strategy_team / DG / superuser → StrategyStack/StrategyDashboard
   */
  const navigateToHome = useCallback(() => {
    if (usesStrategyStack) {
      navigation.navigate('StrategyStack' as any, { screen: 'StrategyDashboard' });
    } else {
      navigation.navigate('DashboardStack' as any, { screen: 'Dashboard' });
    }
  }, [navigation, usesStrategyStack]);

  /**
   * Navigate to Goals list based on user role
   */
  const navigateToGoals = useCallback(() => {
    if (usesStrategyStack) {
      navigation.navigate('StrategyStack' as any, { screen: 'Goals' });
    } else {
      navigation.navigate('GoalsStack' as any, { screen: 'Goals' });
    }
  }, [navigation, usesStrategyStack]);

  /**
   * Navigate to GoalDetail based on user role
   */
  const navigateToGoalDetail = useCallback((goalId: number) => {
    if (usesStrategyStack) {
      navigation.navigate('StrategyStack' as any, {
        screen: 'GoalDetail',
        params: { id: goalId },
      });
    } else {
      navigation.navigate('GoalsStack' as any, {
        screen: 'GoalDetail',
        params: { id: goalId },
      });
    }
  }, [navigation, usesStrategyStack]);

  const navigateToInitiatives = useCallback(() => {
    navigation.navigate('InitiativesStack' as any, { screen: 'Initiatives' });
  }, [navigation]);

  const navigateToInitiativeDetail = useCallback((initiativeId: number) => {
    navigation.navigate('InitiativesStack' as any, {
      screen: 'InitiativeDetail',
      params: { id: initiativeId },
    });
  }, [navigation]);

  const navigateToKPIs = useCallback(() => {
    navigation.navigate('GoalsStack' as any, { screen: 'Goals' });
  }, [navigation]);

  const navigateToKPIDetail = useCallback((kpiId: number) => {
    navigation.navigate('GoalsStack' as any, {
      screen: 'KPIDetail',
      params: { id: kpiId },
    });
  }, [navigation]);

  const navigateToProcesses = useCallback(() => {
    navigation.navigate('ProcessesStack' as any, { screen: 'Processes' });
  }, [navigation]);

  const navigateToProcessDetail = useCallback((processId: number) => {
    navigation.navigate('ProcessesStack' as any, {
      screen: 'ProcessDetail',
      params: { id: processId },
    });
  }, [navigation]);

  const navigateToServices = useCallback(() => {
    navigation.navigate('ServicesStack' as any, { screen: 'Services' });
  }, [navigation]);

  const navigateToMyOrganization = useCallback(() => {
    navigation.navigate('MyOrganizationStack' as any, { screen: 'MyOrganization' });
  }, [navigation]);

  const navigateToUnitGoals = useCallback(() => {
    navigation.navigate('UnitGoalsStack' as any, { screen: 'UnitGoals' });
  }, [navigation]);

  const navigateToRisks = useCallback(() => {
    navigation.navigate('RisksStack' as any, { screen: 'Risks' });
  }, [navigation]);

  /**
   * Check if user can access a route
   */
  const canAccess = useCallback((route: string): boolean => {
    const orgEmployeeRoutes = [
      'Dashboard', 'DashboardStack',
      'Goals', 'GoalsStack', 'GoalDetail', 'AddGoal',
      'Initiatives', 'InitiativesStack', 'InitiativeDetail', 'AddInitiative',
      'KPIs', 'KPIsStack', 'KPIDetail', 'AddKPIReading',
      'Processes', 'ProcessesStack', 'ProcessDetail', 'AddProcess',
      'Services', 'ServicesStack', 'ServiceDetail', 'AddService',
      'UnitGoals', 'UnitGoalsStack', 'UnitGoalDetail', 'AddUnitGoal',
      'Risks', 'RisksStack', 'AddRisk',
      'MyOrganization', 'MyOrganizationStack',
      'Notifications',
    ];

    const strategyTeamRoutes = [
      ...orgEmployeeRoutes,
      'StrategyStack', 'StrategyDashboard',
      'Organizations', 'OrganizationsStack', 'OrganizationDetail',
      'StrategyOrganizationsStack',
      'Schedules', 'SchedulesStack',
      'Export', 'ExportStack',
      // Admin / AdminStack removed 2026-05-13 — DTS-SSO owns user mgmt.
    ];

    const orgExecutiveRoutes = [
      'Dashboard', 'DashboardStack',
      'Goals', 'GoalsStack', 'GoalDetail',
      'Initiatives', 'InitiativesStack', 'InitiativeDetail',
      'KPIs', 'KPIsStack', 'KPIDetail',
      'Processes', 'ProcessesStack', 'ProcessDetail',
      'Services', 'ServicesStack', 'ServiceDetail',
      'UnitGoals', 'UnitGoalsStack', 'UnitGoalDetail',
      'Risks', 'RisksStack',
      'Employees', 'EmployeesStack',
      'MyOrganization', 'MyOrganizationStack',
      'Notifications',
      'Settings', 'SettingsStack',
      'Help', 'HelpStack',
    ];

    const executiveRoutes = [
      'StrategyStack', 'StrategyDashboard',
      'Goals', 'GoalsStack', 'GoalDetail',
      'Initiatives', 'InitiativesStack', 'InitiativeDetail',
      'KPIs', 'KPIsStack', 'KPIDetail',
      'Organizations', 'OrganizationsStack', 'OrganizationDetail',
      'StrategyOrganizationsStack',
      'Risks', 'RisksStack',
      'Schedules', 'SchedulesStack',
      'Help', 'HelpStack',
    ];

    if (isSuperuser || isStrategyTeam) {
      return strategyTeamRoutes.includes(route);
    }

    if (isOrgExecutive) {
      return orgExecutiveRoutes.includes(route);
    }

    if (isDecisionMaker) {
      return executiveRoutes.includes(route);
    }

    if (isOrgEmployee) {
      if (!orgEmployeeRoutes.includes(route)) return false;
      // When data entry is closed, block Add/Edit routes (except AddKPIReading)
      if (!dataEntryOpen) {
        const blockedRoutes = ['AddGoal', 'AddInitiative', 'AddProcess', 'AddService', 'AddUnitGoal', 'AddRisk'];
        if (blockedRoutes.includes(route)) return false;
      }
      return true;
    }

    return false;
  }, [isSuperuser, isStrategyTeam, isOrgExecutive, isDecisionMaker, isOrgEmployee, dataEntryOpen]);

  return {
    // Navigation functions
    navigateToHome,
    navigateToGoals,
    navigateToGoalDetail,
    navigateToInitiatives,
    navigateToInitiativeDetail,
    navigateToKPIs,
    navigateToKPIDetail,
    navigateToProcesses,
    navigateToProcessDetail,
    navigateToServices,
    navigateToMyOrganization,
    navigateToUnitGoals,
    navigateToRisks,
    canAccess,
    // Permission flags for different roles
    canAdd,
    canEdit,
    canApprove,
    canDelete,
    isViewOnly,
    isDecisionMaker,
    isOrgExecutive,
    isStrategyTeam,
    isOrgEmployee,
    isDataEntryClosed,
    canViewAllOrganizations,
    canSeePending,
    canSeeNotifications,
    canExport,
    canComment,
  };
}
